import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Formik, Form as FormikForm, useFormikContext } from 'formik';

import { Alert, Breadcrumb, Button, NavLinkButton, Panel } from '@shared/components/bootstrap';
import { FormikFileFormGroup } from '@shared/components/fields/formik/formik_file_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikResizableTextAreaFormGroup } from '@shared/components/fields/formik/formik_resizable_text_area_form_group';
import { FormikSelectFormGroup } from '@shared/components/fields/formik/formik_select_form_group';

import { FormikHTMLFormGroup } from '@admin/components/fields/formik/formik_html_form_group';
import { PageHeader } from '@admin/components/helpers/page_header';
import { LandingLocationBenefitsPanel } from '@admin/components/landing/location/benefits/panel';
import { LandingLocationCardsPanel } from '@admin/components/landing/location/cards/panel';
import { LandingLocationExplanationsPanel } from '@admin/components/landing/location/explanations/panel';
import { LandingLocationFactsPanel } from '@admin/components/landing/location/facts/panel';
import { LandingLocationReviewsPanel } from '@admin/components/landing/location/reviews/panel';
import { LandingLocationTopicsPanel } from '@admin/components/landing/location/topics/panel';
import { Spinner } from '@admin/components/spinner';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  Landing__Location__ExplanationInput,
  Landing__Location__Illustration,
  useLandingLocationPageQuery,
  useLandingLocationPageUpsertMutation,
  useRegionsQuery,
  type Landing__Location__BenefitInput,
  type Landing__Location__Page,
  type Landing__Location__PageInput,
  type LandingLocationPageQuery,
} from '@admin/schema';

const DEFAULT_BENEFITS: Array<Landing__Location__BenefitInput> = [
  {
    title: 'Digital inventory',
    description:
      'Manage your items with our secure online portal to track every item with photos, for easy on-demand and delivery scheduling',
    alt: 'An illustration of a phone w/ pictures of various household items.',
    illustration: Landing__Location__Illustration.Inventory,
  },
  {
    title: 'No driving or parking',
    description: 'We’ll take from here (or to there) so get in your sweatpants, not your car',
    alt: 'An illustration of a Clutter branded truck for moving and storage.',
    illustration: Landing__Location__Illustration.Truck,
  },
  {
    title: 'Controlled & secure',
    description: 'With Clutter’s controlled and super secure locations, your precious items get the VIP treatment',
    alt: 'An illustration of a storage unit with boxes and a lock.',
    illustration: Landing__Location__Illustration.Unit,
  },
];

const DEFAULT_EXPLANATIONS: Array<Landing__Location__ExplanationInput> = [
  {
    title: 'Pack up and pick up',
    description: 'You pack or we pack! Our pros handle every item with care for a seamless, on-time pickup.',
    illustration: Landing__Location__Illustration.Boxes,
    alt: 'Some packing boxes.',
  },
  {
    title: 'VIP vault',
    description:
      'With low damage rates and 24/7 monitoring, your items stay secure in our high tech facilities - big upgrade!',
    illustration: Landing__Location__Illustration.Unit,
    alt: 'A storage unit.',
  },
  {
    title: 'Digital-first inventory',
    description:
      'Even from the couch, one click in your online account portal summons whatever you need back to your door.',
    illustration: Landing__Location__Illustration.Clock,
    alt: 'A clock.',
  },
];

const FormikRegionFormGroup: React.FC<{
  id: string;
  name: string;
  label: string;
}> = (props) => {
  const { data, loading } = useRegionsQuery({ client });

  return (
    <FormikSelectFormGroup disabled={loading} {...props}>
      <option value=""> - Region - </option>
      {data?.regions.map((region) => (
        <option key={region.id} value={region.id}>
          {region.name}
        </option>
      ))}
    </FormikSelectFormGroup>
  );
};

type FormInput = Omit<Landing__Location__PageInput, 'latitude' | 'longitude'> & {
  latitude: string;
  longitude: string;
};

const LandingLocationPageFormFields: React.FC = () => {
  const {
    values: { slug },
  } = useFormikContext<FormInput>();
  return (
    <>
      <FormikRegionFormGroup id="regionID" name="regionID" label="Region:" />
      <FormikInputFormGroup id="slug" name="slug" label="Slug:" help={`https://www.clutter.com/locations/${slug}`} />
      <FormikResizableTextAreaFormGroup id="title" name="title" label="Title:" />
      <FormikResizableTextAreaFormGroup id="description" name="description" label="Description:" />
      <FormikInputFormGroup id="latitude" name="latitude" label="Latitude:" type="number" />
      <FormikInputFormGroup id="longitude" name="longitude" label="Longitude:" type="number" />
      <FormikFileFormGroup id="hero" name="hero" label="Hero:" accept="image/*" />
      <FormikHTMLFormGroup id="blurb" name="blurb" label="Blurb:" />
      <FormikInputFormGroup id="cta" name="cta" label="CTA:" placeholder="Get Pricing" />
      <FormikResizableTextAreaFormGroup id="benefitsTitle" name="benefitsTitle" label="Benefits Title:" />
      <FormikResizableTextAreaFormGroup id="reviewsTitle" name="reviewsTitle" label="Reviews Title:" />
      <FormikResizableTextAreaFormGroup id="serviceAreaTitle" name="serviceAreaTitle" label="Service Area Title:" />
      <FormikResizableTextAreaFormGroup
        id="serviceAreaSubtitle"
        name="serviceAreaSubtitle"
        label="Service Area Subtitle:"
      />
      <FormikResizableTextAreaFormGroup id="topicsTitle" name="topicsTitle" label="FAQ Title:" />
    </>
  );
};

const LandingLocationPageFormContent: React.FC<{
  page?: LandingLocationPageQuery['page'];
  onSave(page: Pick<Landing__Location__Page, 'id'>): void;
}> = ({ page, onSave }) => {
  const [save, { loading, data }] = useLandingLocationPageUpsertMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.resource) onSave(result.resource);
    },
  });

  const onSubmit = (input: FormInput) => {
    const latitude = Number(input.latitude) || undefined;
    const longitude = Number(input.longitude) || undefined;
    if (!latitude || !longitude) return;

    const response = save({
      variables: {
        id: page?.id,
        input: {
          ...input,
          latitude,
          longitude,
        },
      },
    });
  };

  return (
    <Formik<FormInput>
      initialValues={{
        regionID: page?.region.id ?? '',
        slug: page?.slug ?? '',
        title: page?.title ?? '',
        description: page?.description ?? '',
        blurb: page?.trixHTML ?? '',
        cta: page?.cta ?? 'Get Pricing',
        latitude: page ? String(page.latitude) : '',
        longitude: page ? String(page.longitude) : '',
        benefitsTitle: page?.benefitsTitle ?? 'Why Clutter?',
        reviewsTitle: page?.reviewsTitle ?? 'Your neighbors are into Clutter too',
        serviceAreaTitle: page?.serviceAreaTitle ?? 'Do you serve my area?',
        serviceAreaSubtitle: page?.serviceAreaSubtitle ?? '',
        topicsTitle: page?.topicsTitle ?? 'FAQ',
        benefits: page
          ? page.benefits.map((benefit) => ({
              id: benefit.id,
              title: benefit.title,
              description: benefit.description,
              illustration: benefit.illustration,
              alt: benefit.alt,
            }))
          : DEFAULT_BENEFITS,
        cards:
          page?.cards?.map((card) => ({
            id: card.id,
            title: card.title,
            description: card.description,
            alt: card.alt,
          })) ?? [],
        explanations: page
          ? page.explanations.map((explanation) => ({
              id: explanation.id,
              title: explanation.title,
              description: explanation.description,
              alt: explanation.alt,
              illustration: explanation.illustration,
            }))
          : DEFAULT_EXPLANATIONS,
        facts:
          page?.facts?.map((fact) => ({
            id: fact.id,
            title: fact.title,
            description: fact.description,
          })) ?? [],
        reviews:
          page?.reviews?.map((review) => ({
            id: review.id,
            author: review.author,
            message: review.message,
            rating: review.rating,
          })) ?? [],
        topics:
          page?.topics?.map((topic) => ({
            id: topic.id,
            name: topic.name,
            blurb: topic.trixHTML,
          })) ?? [],
      }}
      onSubmit={onSubmit}
    >
      <FormikForm>
        <Panel>
          <Panel.Body>
            {data?.result?.error && <Alert style="danger">{data.result.error}</Alert>}
            <LandingLocationPageFormFields />
          </Panel.Body>
        </Panel>

        <LandingLocationBenefitsPanel />
        <LandingLocationFactsPanel />
        <LandingLocationExplanationsPanel />
        <LandingLocationReviewsPanel />
        <LandingLocationTopicsPanel />
        <LandingLocationCardsPanel />

        <Panel>
          <Panel.Footer align="right">
            <NavLinkButton disabled={loading} kind="default" to="/landing/location/pages">
              Cancel
            </NavLinkButton>
            <Button loading={loading} kind="primary" type="submit">
              Save
            </Button>
          </Panel.Footer>
        </Panel>
      </FormikForm>
    </Formik>
  );
};

export const LandingLocationPageForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  useTitle(id ? 'Landing - Location - Pages - Edit' : 'Landing - Location - Pages - New');
  const navigate = useNavigate();
  const onSave = (page: Pick<Landing__Location__Page, 'id'>) => {
    navigate(`/landing/location/pages/${page.id}`);
  };

  const { data } = useLandingLocationPageQuery({
    client,
    fetchPolicy: 'network-only',
    variables: { id: id! },
    skip: !id,
  });
  const page = data?.page;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>Landing</Breadcrumb.Item>
          <Breadcrumb.Item>Location</Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to="/landing/location/pages">Pages</NavLink>
          </Breadcrumb.Item>
          {id && (
            <Breadcrumb.Item>
              <NavLink to={`/landing/location/pages/${id}`}>{page?.title ?? '-'}</NavLink>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item active>{id ? 'Edit' : 'New'}</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      {!id || page ? <LandingLocationPageFormContent page={page} onSave={onSave} /> : <Spinner />}
    </>
  );
};
